import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Plus,
  User,
  Briefcase,
  Heart,
  Users,
  FileText,
  Target,
  Tag,
  Network,
  Edit
} from 'lucide-react';

const ContactProfile = ({ contact, onAddInteraction, onEditInteraction }) => {
  const formatPhone = (phone) => {
    if (!phone || !phone.number) return null;
    return `+${phone.country_code || ''} ${phone.number}`;
  };

  const sortedInteractions = [...(contact.interactions || [])].sort((a, b) => {
    const dateA = a.date || a.created_at;
    const dateB = b.date || b.created_at;
    return new Date(dateB) - new Date(dateA);
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header Card */}
      <Card className="border-l-4 border-l-[#D4AF37]">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-heading font-semibold text-[#0F172A] mb-2">
                {contact.name}
              </h1>
              {contact.job_title && (
                <p className="text-lg text-slate-600 mb-1">{contact.job_title}</p>
              )}
              {contact.designation && (
                <p className="text-slate-500">{contact.designation}</p>
              )}
              
              {/* Tags */}
              {contact.custom_tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {contact.custom_tags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-[#D4AF37]/10 text-[#8A7120] border-[#D4AF37]/30"
                      data-testid={`profile-tag-${index}`}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={onAddInteraction}
              className="gap-2 bg-[#D4AF37] hover:bg-[#B08D28] text-white"
              data-testid="add-interaction-button"
            >
              <Plus className="w-4 h-4" />
              Add Next Interaction Notes
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Basic Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Contact Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4 text-[#D4AF37]" />
                Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {contact.current_employer && (
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Employer</p>
                    <p className="text-slate-700">{contact.current_employer}</p>
                  </div>
                </div>
              )}
              
              {contact.city_based_in && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">City</p>
                    <p className="text-slate-700">{contact.city_based_in}</p>
                  </div>
                </div>
              )}
              
              {contact.email_address && (
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Email</p>
                    <a 
                      href={`mailto:${contact.email_address}`}
                      className="text-[#D4AF37] hover:underline"
                    >
                      {contact.email_address}
                    </a>
                  </div>
                </div>
              )}
              
              {formatPhone(contact.phone_numbers?.handphone) && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Handphone</p>
                    <p className="text-slate-700">{formatPhone(contact.phone_numbers.handphone)}</p>
                  </div>
                </div>
              )}
              
              {formatPhone(contact.phone_numbers?.work) && (
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Work</p>
                    <p className="text-slate-700">{formatPhone(contact.phone_numbers.work)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Former Employers */}
          {contact.former_employers?.filter(e => e).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                  Former Employers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {contact.former_employers.filter(e => e).map((employer, index) => (
                    <li key={index} className="text-slate-600 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
                      {employer}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Hobbies & Passions */}
          {contact.hobbies_passions?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <Heart className="w-4 h-4 text-[#D4AF37]" />
                  Hobbies & Passions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {contact.hobbies_passions.map((item, index) => (
                    <div key={index} className="border-l-2 border-slate-200 pl-3">
                      <p className="font-medium text-slate-700">{item.name}</p>
                      {item.remarks && (
                        <p className="text-sm text-slate-500">{item.remarks}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Family Information */}
          {contact.family_information?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <Users className="w-4 h-4 text-[#D4AF37]" />
                  Family Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {contact.family_information.map((item, index) => (
                    <div key={index} className="border-l-2 border-slate-200 pl-3">
                      <p className="font-medium text-slate-700">{item.name}</p>
                      {item.remarks && (
                        <p className="text-sm text-slate-500">{item.remarks}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Details & History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Background */}
          {contact.brief_background?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <FileText className="w-4 h-4 text-[#D4AF37]" />
                  Background
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {contact.brief_background.map((item, index) => (
                    <li key={index} className="text-slate-600 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Common Network */}
          {contact.common_network?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <Users className="w-4 h-4 text-[#D4AF37]" />
                  Common Network
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-sm font-medium text-slate-500">Name</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-slate-500">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contact.common_network.map((item, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-2 px-3 text-slate-700">{item.name}</td>
                          <td className="py-2 px-3 text-slate-600">{item.remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Key Contacts in Network */}
          {contact.key_contacts_in_network?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <Network className="w-4 h-4 text-[#D4AF37]" />
                  Key Contacts in Network
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-sm font-medium text-slate-500">Name</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-slate-500">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contact.key_contacts_in_network.map((item, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-2 px-3 text-slate-700">{item.name}</td>
                          <td className="py-2 px-3 text-slate-600">{item.company}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Course of Action */}
          {contact.next_course_of_action?.length > 0 && (
            <Card className="border-[#D4AF37]/30">
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  Next Course of Action
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {contact.next_course_of_action.map((item, index) => (
                    <li key={index} className="text-slate-600 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Other Remarks */}
          {contact.other_remarks?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                  <FileText className="w-4 h-4 text-[#D4AF37]" />
                  Other Remarks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {contact.other_remarks.map((item, index) => (
                    <li key={index} className="text-slate-600 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Interaction History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium flex items-center gap-2 text-slate-700">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                Interaction History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sortedInteractions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No interactions recorded yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onAddInteraction}
                    className="mt-4 gap-2"
                    data-testid="add-first-interaction-button"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Interaction
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedInteractions.map((interaction, index) => (
                    <div
                      key={interaction.id || index}
                      className="border-l-2 border-[#D4AF37] pl-4 py-2 animate-slideIn"
                      style={{ animationDelay: `${index * 100}ms` }}
                      data-testid={`interaction-${index}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-slate-400">
                            {interaction.date || format(new Date(interaction.created_at), 'yyyy-MM-dd')}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {interaction.is_summary ? 'Quick Note' : interaction.interaction_type}
                          </Badge>
                          {interaction.city && (
                            <span className="text-sm text-slate-500">
                              in {interaction.city}
                              {interaction.location && ` at ${interaction.location}`}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditInteraction(interaction)}
                          className="h-7 w-7 text-slate-400 hover:text-[#D4AF37]"
                          data-testid={`edit-interaction-${index}`}
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      
                      {interaction.setup_method && (
                        <p className="text-xs text-slate-400 mb-2">
                          {interaction.setup_method === 'reached_out_by_them' && 'They reached out'}
                          {interaction.setup_method === 'reached_out_by_me' && 'I reached out'}
                          {interaction.setup_method === 'connected_by' && `Connected by ${interaction.connected_by}`}
                        </p>
                      )}
                      
                      {interaction.is_summary ? (
                        <p className="text-slate-600">{interaction.summary_text}</p>
                      ) : (
                        <>
                          <div
                            className="text-slate-600 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: interaction.key_points }}
                          />
                          {interaction.questions_asked && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Questions Asked</p>
                              <div
                                className="text-slate-600 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: interaction.questions_asked }}
                              />
                            </div>
                          )}
                          {interaction.quotes && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Quotes</p>
                              <div
                                className="text-slate-600 prose prose-sm max-w-none italic"
                                dangerouslySetInnerHTML={{ __html: interaction.quotes }}
                              />
                            </div>
                          )}
                          {interaction.future_course_of_action && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-wide mb-1">Future Course of Action</p>
                              <div
                                className="text-slate-600 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: interaction.future_course_of_action }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactProfile;
